<template>
  <div class="activity-container">
    <FriedEmptyState
      v-if="noActivity || error"
      class="empty-state"
      :title="noActivitiesInfo.title"
      :description="noActivitiesInfo.description"
      :icon="noActivitiesInfo.icon"
    >
      <template #cta>
        <FriedButton
          v-if="noActivitiesInfo.showCta"
          data-external="no-activity-cta-btn"
          @click="handleCta"
        >
          {{ ctaButtonText }}
        </FriedButton>
      </template>
    </FriedEmptyState>
    <template v-else>
      <div
        v-for="notificationDay in sortedNotifications"
        :key="notificationDay.date"
        class="activity-day-container"
      >
        <div class="header">
          <DateBadge :date="getDateInMillis(notificationDay.date)" />
          <FriedButton
            v-if="isMostRecentDate(notificationDay.date) && hasUnreadNotifications"
            :data-external="`activity-mark-all-as-read-button-${notificationDay.date}`"
            class="header-button"
            button-type="secondary"
            @click="markAllAsRead"
          >
            {{ t('mark_all_below_as_read_cta') }}
          </FriedButton>
        </div>
        <ActivityCard
          :notifications="notificationDay.notifications"
          :room-settings="roomSettings"
          :participants="participants"
          @navigate-to="$emit('navigate-to', $event)"
          @set-viewed="$emit('set-viewed', $event)"
        />
      </div>
      <FriedSpinner v-if="hasMore" ref="loadMoreLoader" />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { DateTime } from 'luxon';
import { useI18n } from 'vue-i18n';
import { useIntersectionObserver } from '@vueuse/core';
import { ActivityTab } from '../enums/activity-tabs';
import type { DealroomSettings } from '../../types/dsr';
import NotificationHelper from '../helpers/notification.helper';
import type { Participant } from '../../types/participant';
import DateBadge from '../../components/DateBadge.vue';
import { DealroomEventType } from '../../api/timeline/types/log.types';
import type { NotificationEvent } from '../types';
import ActivityCard from './ActivityCard.vue';

const {
  notifications,
  loading,
  error,
  roomSettings = null,
  participants,
  hasMore,
  showRoomLink,
  isViewer,
  selectedTab,
} = defineProps<{
  notifications: NotificationEvent[];
  loading: boolean;
  error: boolean;
  roomSettings: DealroomSettings;
  participants: Participant[];
  hasMore: boolean;
  showRoomLink?: boolean;
  isViewer?: boolean;
  selectedTab: ActivityTab;
}>();

const emit = defineEmits<{
  'navigate-to': [NotificationEvent];
  'load-more': [];
  'set-viewed': [string];
  'set-all-viewed': [ActivityTab];
  'go-to-room': [];
}>();

const { t } = useI18n();
const loadMoreLoader = ref<HTMLDivElement | null>(null);

const notificationsByTab = computed(() => {
  switch (selectedTab) {
    case ActivityTab.Chat:
      return notifications.filter(
        notification => notification.type === DealroomEventType.ConversationMessageCreated
      );
    case ActivityTab.Comments:
      return notifications.filter(notification =>
        [
          DealroomEventType.ContextualCommentReplyCreated,
          DealroomEventType.ContextualCommentUpdated,
        ].includes(notification.type)
      );
    case ActivityTab.Other:
      return notifications.filter(
        notification =>
          ![
            DealroomEventType.ConversationMessageCreated,
            DealroomEventType.ContextualCommentReplyCreated,
            DealroomEventType.ContextualCommentUpdated,
          ].includes(notification.type)
      );
    default:
      return notifications;
  }
});

const sortedNotifications = computed(() =>
  NotificationHelper.organizeNotificationsByDate(notificationsByTab.value)
);

const noActivity = computed(() => !sortedNotifications.value.length && !loading);

const hasUnreadNotifications = computed(() =>
  sortedNotifications.value.some(sortedNotification =>
    sortedNotification.notifications.some(notification => !notification.isRead)
  )
);

const ctaButtonText = computed(() => (error ? t('refresh-page') : t('go_to_room_cta')));

const handleCta = () => {
  if (error) {
    window.location.reload();
  }
  emit('go-to-room');
};

const emptyStateDescription = computed(() => {
  if (!notifications.length) {
    return t('activity_sheet_no_activity_description');
  }
  if (selectedTab === ActivityTab.Chat) {
    return t('dr_some_activity_chat_description');
  }
  return t('dr_some_activity_comments_description');
});

const emptyStateTitle = computed(() =>
  !notifications.length ? t('activity_sheet_no_activity_title') : t('dr_some_activity_chat_title')
);

const noActivitiesInfo = computed(() => {
  if (error) {
    return {
      title: t('activity_sheet_no_data_title'),
      description: t('activity_sheet_no_data_description'),
      showCta: true,
      icon: 'face-with-monocle',
    };
  }
  if (isViewer) {
    return {
      title: t('dr_no_activity_title'),
      description: t('dr_no_activity_description'),
      showCta: false,
      icon: 'raising-hands',
    };
  }
  if ([ActivityTab.Chat, ActivityTab.Comments].includes(selectedTab)) {
    return {
      title: emptyStateTitle.value,
      description: emptyStateDescription.value,
      showCta: showRoomLink,
      icon: 'sleeping-face',
    };
  }
  return {
    title: t('activity_sheet_no_activity_title'),
    description: t('activity_sheet_no_activity_description'),
    showCta: showRoomLink,
    icon: 'sleeping-face',
  };
});

const getDateInMillis = (date: string) => DateTime.fromFormat(date, 'yyyy-MM-dd').toMillis();

const isMostRecentDate = (date: string) => date === sortedNotifications.value[0]?.date;

const markAllAsRead = () => {
  emit('set-all-viewed', selectedTab);
};

useIntersectionObserver(loadMoreLoader, ([{ isIntersecting }]) => {
  if (!isIntersecting) return;
  if (hasMore) {
    setTimeout(() => {
      emit('load-more');
    }, 1000);
  }
});
</script>

<style lang="scss" scoped>
.activity-container {
  .empty-state {
    margin: 0 auto;
    height: 100%;
    justify-content: center;
  }

  .activity-day-container {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-50);

    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;

      button {
        height: fit-content;
      }
    }
  }
}
</style>
